'use client'

/**
 * The cookie banner and the "Cookie settings" dialog. Mounted once, in the
 * root layout; renders nothing where MaiTeams' own tracking never runs (a
 * capability link, a customer's published page, a white-label host), because
 * there is nothing there to consent to.
 *
 * The banner shows only to a visitor who must be asked (the EU, the EEA, the
 * UK and Switzerland, or an unknown location) and has not chosen. Accept,
 * Reject and Settings are the same size and weight: refusing is exactly as
 * easy as accepting. The dialog opens from the banner, the site footer and
 * the Cookie Policy (`openCookieSettings`), in every region, so anyone can
 * change their mind.
 *
 * The model and the storage rules live in `lib/preferences/cookie-consent.ts`,
 * the live state in `lib/privacy/consent-runtime.ts`.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { Cookie, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/components/auth-provider'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import {
  ensureCookieConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  openCookieSettings,
  setCookieChoice,
  syncCookieConsentWithAccount,
} from '@/lib/privacy/consent-runtime'
import { useCookieConsent, useFirstPartyTrackingSurface } from '@/lib/privacy/use-cookie-consent'
import type { AnalyticsChoice } from '@/lib/preferences/cookie-consent'

/** The v0 preview's stand-in account (components/auth-provider.tsx): it has no row to save to. */
const V0_PREVIEW_USER_ID = 'v0-preview-user'

export function CookieConsent() {
  const surface = useFirstPartyTrackingSurface()
  const consent = useCookieConsent()
  const { user, loading } = useAuth()
  const userId = user && user.id !== V0_PREVIEW_USER_ID ? user.id : null
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [analyticsDraft, setAnalyticsDraft] = useState(false)
  const [draftEdited, setDraftEdited] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (surface) void ensureCookieConsent()
  }, [surface])

  useEffect(() => {
    if (surface && !loading) void syncCookieConsentWithAccount(userId)
  }, [surface, userId, loading])

  const openSettings = useCallback(() => {
    setAnalyticsDraft(consent.permissions.analytics)
    setDraftEdited(false)
    setSettingsOpen(true)
  }, [consent.permissions.analytics])

  useEffect(() => {
    const onOpen = () => openSettings()
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen)
  }, [openSettings])

  useEffect(() => {
    if (settingsOpen && !draftEdited) setAnalyticsDraft(consent.permissions.analytics)
  }, [settingsOpen, draftEdited, consent.permissions.analytics])

  const decide = useCallback(
    async (analytics: AnalyticsChoice) => {
      setSaving(true)
      try {
        await setCookieChoice(analytics, { userId: loading ? undefined : userId })
      } finally {
        setSaving(false)
        setSettingsOpen(false)
      }
    },
    [userId, loading]
  )

  if (!surface || !consent.ready) return null

  const showBanner = consent.needsDecision && consent.consentRequired !== null && !settingsOpen

  return (
    <>
      {showBanner && (
        <section
          aria-label="Cookie choices"
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-xl border border-border bg-card p-4 text-card-foreground shadow-lg sm:inset-x-auto sm:left-4 sm:bottom-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Cookie className="h-4 w-4" aria-hidden="true" />
            </span>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We&apos;d like to use analytics cookies to see how MaiTeams is used. They stay off unless you accept.{' '}
              <Link href="/cookies" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
                Cookie Policy
              </Link>
            </p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Button variant="outline" size="sm" disabled={saving} onClick={() => void decide('denied')}>
              Reject
            </Button>
            <Button variant="outline" size="sm" disabled={saving} onClick={openSettings}>
              Settings
            </Button>
            <Button variant="outline" size="sm" disabled={saving} onClick={() => void decide('granted')}>
              Accept
            </Button>
          </div>
        </section>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle>Cookie settings</DialogTitle>
            <DialogDescription>
              Choose which optional cookies MaiTeams may use in this browser. You can change this at any time.{' '}
              <Link href="/cookies" className="underline underline-offset-2 hover:text-foreground" onClick={() => setSettingsOpen(false)}>
                Cookie Policy
              </Link>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div className="space-y-1">
                <p id="cookie-essential-label" className="text-sm font-medium text-foreground">Essential</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Sign-in, security, error reports and remembering your choices. Always on.
                </p>
              </div>
              <Switch checked disabled aria-labelledby="cookie-essential-label" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div className="space-y-1">
                <p id="cookie-analytics-label" className="text-sm font-medium text-foreground">Analytics</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Google Analytics, PostHog and New Relic show us how the site is used, including session replays with the text
                  masked. Sentry measures page speed. Dub attributes referrals to our partners.
                </p>
              </div>
              <Switch
                checked={analyticsDraft}
                onCheckedChange={(checked) => { setDraftEdited(true); setAnalyticsDraft(checked) }}
                disabled={saving}
                aria-labelledby="cookie-analytics-label"
              />
            </div>
            {consent.gpc && (
              <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>
                  Global Privacy Control is on in this browser, and we honour it: Google Analytics and referral tracking stay
                  off here whatever you choose.
                </span>
              </p>
            )}
          </div>

          <DialogFooter className="grid grid-cols-3 gap-2 sm:flex sm:justify-end sm:space-x-0">
            <Button variant="outline" size="sm" disabled={saving} onClick={() => void decide('denied')}>
              Reject all
            </Button>
            <Button variant="outline" size="sm" disabled={saving} onClick={() => void decide('granted')}>
              Accept all
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => void decide(analyticsDraft ? 'granted' : 'denied')}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** A text button that opens the settings dialog; the site footer and the Cookie Policy use it. */
export function CookieSettingsButton({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <button
      type="button"
      className={className}
      onClick={openCookieSettings}
    >
      {children ?? 'Cookie settings'}
    </button>
  )
}
